/**
 * SizilPro — WizardStep4Review
 * Step 4 of the proposal wizard: review AI-generated content.
 *
 * Updated to reflect richer Template → Proposal handoff:
 * - builder mode visibility
 * - fixed-page-aware template defaults
 * - clearer review of what came from the template
 *
 * File: src/modules/proposal-builder/components/wizard/WizardStep4Review.tsx
 */

import { useMemo, useState } from "react";
import {
  Loader2,
  ArrowLeft,
  Check,
  Pencil,
  X,
  ChevronDown,
  ChevronUp,
  LayoutTemplate,
  Move,
  Pin,
  PanelsTopLeft,
  Sparkles,
  FileText,
  Tag,
  PenLine,
  DollarSign,
  Image,
} from "lucide-react";
import { cn } from "@/lib/utils";
import type {
  WizardAnswers,
  WizardLineItem,
} from "../../hooks/useProposalWizard";
import type { BuilderMode, FixedPagePolicy } from "../../types";

interface Props {
  answers: WizardAnswers;
  isCreating: boolean;
  isGenerating?: boolean;
  onUpdateLineItem: (
    localId: string,
    updates: Partial<WizardLineItem>
  ) => void;
  onUpdateAnswers: (updates: Partial<WizardAnswers>) => void;
  onConfirm: () => void;
  onBack: () => void;
  confirmLabel?: string;
}

const BUILDER_MODE_CONFIG: Record<
  BuilderMode,
  {
    label: string;
    icon: React.ReactNode;
    color: string;
    description: string;
  }
> = {
  prebuilt: {
    label: "Pre-built",
    icon: <LayoutTemplate className="h-4 w-4" />,
    color: "text-cyan-300 bg-cyan-500/10 border-cyan-500/20",
    description:
      "This proposal started from a structured reusable template blueprint.",
  },
  drag_drop: {
    label: "Drag & drop",
    icon: <Move className="h-4 w-4" />,
    color: "text-fuchsia-300 bg-fuchsia-500/10 border-fuchsia-500/20",
    description:
      "This proposal started from a template marked for the richer builder direction.",
  },
};

const FIXED_PAGE_POLICY_CONFIG: Record<
  FixedPagePolicy,
  {
    label: string;
    icon: React.ReactNode;
    color: string;
    description: string;
  }
> = {
  template_defined: {
    label: "Template pages",
    icon: <PanelsTopLeft className="h-4 w-4" />,
    color: "text-white/45 bg-white/5 border-white/10",
    description:
      "This proposal mainly relies on pages chosen directly by the template.",
  },
  brand_defined: {
    label: "Brand-defined fixed pages",
    icon: <Pin className="h-4 w-4" />,
    color: "text-amber-300 bg-amber-500/10 border-amber-500/20",
    description:
      "This proposal inherits fixed pages from Brand Setup.",
  },
  mixed: {
    label: "Mixed fixed pages",
    icon: <Pin className="h-4 w-4" />,
    color: "text-emerald-300 bg-emerald-500/10 border-emerald-500/20",
    description:
      "This proposal inherits both template-chosen pages and brand-defined fixed pages.",
  },
};

function getBuilderModeLabel(mode: BuilderMode) {
  return BUILDER_MODE_CONFIG[mode] ?? BUILDER_MODE_CONFIG.prebuilt;
}

function getFixedPagePolicyLabel(policy: FixedPagePolicy) {
  return FIXED_PAGE_POLICY_CONFIG[policy] ?? FIXED_PAGE_POLICY_CONFIG.template_defined;
}

export function WizardStep4Review({
  answers,
  isCreating,
  onUpdateLineItem,
  onUpdateAnswers,
  onConfirm,
  onBack,
  confirmLabel = "Looks good — save draft",
}: Props) {
  const subtotal = answers.lineItems.reduce(
    (sum, i) => sum + (i.price ?? 0),
    0
  );
  const taxAmount = subtotal * (answers.taxRate ?? 0);
  const total = subtotal + taxAmount;

  const fmt = (n: number) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(n);

  const builderCfg = getBuilderModeLabel(answers.templateBuilderMode);
  const fixedCfg = getFixedPagePolicyLabel(answers.templateFixedPagePolicy);

  const templateSummary = useMemo(() => {
    const templateName = answers.template?.name ?? "No template selected";
    const templateDescription =
      answers.template?.description?.trim() ||
      "No template description added yet.";

    return {
      templateName,
      templateDescription,
      staticPageCount: Array.from(
        new Set(
          answers.templateStaticPageIds.filter(
            (pageId): pageId is string =>
              typeof pageId === "string" && pageId.trim().length > 0
          )
        )
      ).length,
      serviceCount: answers.lineItems.length,
      aiTone: answers.template?.ai_tone ?? null,
    };
  }, [answers]);

  return (
    <div className="space-y-6">
      <ReviewSection
        title="Template handoff"
        subtitle="This proposal is being created from the selected template foundation"
      >
        <div className="space-y-4">
          <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-4">
            <p className="text-sm font-semibold text-white/85">
              {templateSummary.templateName}
            </p>
            <p className="mt-2 text-sm leading-6 text-white/45">
              {templateSummary.templateDescription}
            </p>

            <div className="mt-4 flex flex-wrap gap-2">
              <span
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium",
                  builderCfg.color
                )}
              >
                {builderCfg.icon}
                {builderCfg.label}
              </span>

              <span
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium",
                  fixedCfg.color
                )}
              >
                {fixedCfg.icon}
                {fixedCfg.label}
              </span>

              {templateSummary.aiTone ? (
                <span className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-black/20 px-3 py-1 text-xs text-white/45">
                  <Sparkles className="h-3.5 w-3.5" />
                  {templateSummary.aiTone}
                </span>
              ) : null}
            </div>
          </div>

          <div className="grid gap-3 md:grid-cols-2">
            <InfoCard
              icon={<Tag className="h-4 w-4" />}
              label="Template services"
              value={`${templateSummary.serviceCount} service${
                templateSummary.serviceCount !== 1 ? "s" : ""
              }`}
              description="These service rows were seeded from the selected template and can still be edited here."
            />
            <InfoCard
              icon={<FileText className="h-4 w-4" />}
              label="Template pages"
              value={`${templateSummary.staticPageCount} page${
                templateSummary.staticPageCount !== 1 ? "s" : ""
              }`}
              description={fixedCfg.description}
            />
          </div>
        </div>
      </ReviewSection>

      <ReviewSection title="Project summary">
        <EditableTextarea
          value={answers.projectSummary}
          placeholder="Project summary will appear here…"
          onChange={(value) => onUpdateAnswers({ projectSummary: value })}
          rows={3}
        />
      </ReviewSection>

      <ReviewSection
        title="Services"
        subtitle={`${answers.lineItems.length} service${
          answers.lineItems.length !== 1 ? "s" : ""
        } seeded from the selected template`}
      >
        {answers.lineItems.length === 0 ? (
          <p className="py-4 text-center text-sm text-white/30">
            No services from template — you can add them after saving.
          </p>
        ) : (
          <div className="space-y-3">
            {answers.lineItems.map((item) => (
              <LineItemReview
                key={item._localId}
                item={item}
                onUpdate={(updates) => onUpdateLineItem(item._localId, updates)}
              />
            ))}
          </div>
        )}
      </ReviewSection>

      <ReviewSection title="Pricing">
        <div className="space-y-3">
          {answers.lineItems
            .filter((i) => i.price != null)
            .map((item) => (
              <div
                key={item._localId}
                className="flex items-center justify-between"
              >
                <span className="mr-4 truncate text-sm text-white/50">
                  {item.name}
                </span>
                <span className="shrink-0 text-sm text-white/70">
                  {fmt(item.price ?? 0)}
                </span>
              </div>
            ))}

          <div className="flex items-center justify-between border-t border-white/[0.06] pt-2">
            <span className="text-sm text-white/40">Tax rate</span>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min={0}
                max={100}
                step={0.1}
                value={((answers.taxRate ?? 0) * 100).toFixed(1)}
                onChange={(e) =>
                  onUpdateAnswers({
                    taxRate: parseFloat(e.target.value) / 100 || 0,
                  })
                }
                className="w-16 rounded-lg border border-white/10 bg-white/5 px-2 py-1 text-right text-sm text-white focus:outline-none focus:ring-1 focus:ring-white/20"
              />
              <span className="text-sm text-white/40">%</span>
            </div>
          </div>

          {(answers.taxRate ?? 0) > 0 && (
            <div className="flex items-center justify-between">
              <span className="text-sm text-white/40">Subtotal</span>
              <span className="text-sm text-white/60">{fmt(subtotal)}</span>
            </div>
          )}

          <div className="flex items-center justify-between border-t border-white/10 pt-2">
            <span className="text-sm font-semibold">Total</span>
            <span className="text-base font-bold">{fmt(total)}</span>
          </div>
        </div>
      </ReviewSection>

      <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] px-5 py-4">
        <p className="mb-3 text-xs uppercase tracking-[0.15em] text-white/30">
          Proposal for
        </p>
        <p className="text-sm font-semibold">{answers.prospectName}</p>
        {answers.prospectEmail && (
          <p className="mt-0.5 text-xs text-white/40">
            {answers.prospectEmail}
          </p>
        )}
        {(answers.prospectCity || answers.prospectState) && (
          <p className="mt-0.5 text-xs text-white/40">
            {[answers.prospectCity, answers.prospectState]
              .filter(Boolean)
              .join(", ")}
          </p>
        )}
      </div>

      {/* Document structure preview */}
      <ReviewSection title="What your PDF will look like">
        <div className="space-y-2">
          <ProposalPagePreview
            icon={<Image className="h-4 w-4" />}
            label="Cover page"
            description={`${answers.prospectName || "Client name"} · company logo · contact info`}
            color="bg-amber-500/10 border-amber-500/20 text-amber-300"
          />
          <ProposalPagePreview
            icon={<FileText className="h-4 w-4" />}
            label="Cover letter"
            description="Your Brand Setup cover letter with signoff"
            color="bg-blue-500/10 border-blue-500/20 text-blue-300"
          />
          <ProposalPagePreview
            icon={<DollarSign className="h-4 w-4" />}
            label="Services & pricing"
            description={`${answers.lineItems.length} service${answers.lineItems.length !== 1 ? "s" : ""} · subtotal · tax · total`}
            color="bg-emerald-500/10 border-emerald-500/20 text-emerald-300"
          />
          <ProposalPagePreview
            icon={<PenLine className="h-4 w-4" />}
            label="Signature page"
            description="Client name · signature field · date"
            color="bg-purple-500/10 border-purple-500/20 text-purple-300"
          />
        </div>
        <p className="mt-3 text-xs text-white/30">
          Pages are generated automatically. Generate the PDF from the proposal detail page to see the final output.
        </p>
      </ReviewSection>

      <div className="rounded-xl border border-amber-500/15 bg-amber-500/5 px-5 py-4">
        <p className="text-sm font-medium text-amber-300">
          Final check before creating the proposal
        </p>
        <p className="mt-1 text-sm leading-6 text-white/45">
          Review what came from the template, adjust any scope descriptions or
          pricing you need, and confirm when this proposal looks right for this customer.
        </p>
      </div>

      <div className="flex items-center justify-between pt-2">
        <button
          type="button"
          onClick={onBack}
          disabled={isCreating}
          className="flex items-center gap-1.5 px-4 py-2.5 text-sm text-white/40 transition-colors hover:text-white/70 disabled:opacity-30"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>

        <button
          type="button"
          onClick={onConfirm}
          disabled={isCreating}
          className="flex items-center gap-2 rounded-xl bg-white px-6 py-2.5 text-sm font-semibold text-black transition-colors hover:bg-white/90 disabled:opacity-50"
        >
          {isCreating ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Saving…
            </>
          ) : (
            <>
              <Check className="h-4 w-4" />
              {confirmLabel}
            </>
          )}
        </button>
      </div>
    </div>
  );
}

function ReviewSection({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="overflow-hidden rounded-xl border border-white/10">
      <div className="border-b border-white/[0.06] bg-white/[0.02] px-5 py-3">
        <p className="text-xs font-semibold uppercase tracking-[0.15em] text-white/40">
          {title}
        </p>
        {subtitle && <p className="mt-0.5 text-xs text-white/25">{subtitle}</p>}
      </div>
      <div className="px-5 py-4">{children}</div>
    </div>
  );
}

function InfoCard({
  icon,
  label,
  value,
  description,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  description: string;
}) {
  return (
    <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-4">
      <div className="flex items-center gap-2 text-white/35">
        {icon}
        <span className="text-xs uppercase tracking-[0.14em]">{label}</span>
      </div>
      <p className="mt-2 text-lg font-semibold text-white/85">{value}</p>
      <p className="mt-2 text-sm leading-6 text-white/40">{description}</p>
    </div>
  );
}

function LineItemReview({
  item,
  onUpdate,
}: {
  item: WizardLineItem;
  onUpdate: (updates: Partial<WizardLineItem>) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [editingDesc, setEditingDesc] = useState(false);
  const [editingPrice, setEditingPrice] = useState(false);
  const [descDraft, setDescDraft] = useState(item.description);
  const [priceDraft, setPriceDraft] = useState(String(item.price ?? ""));

  const fmt = (n: number | null) =>
    n != null
      ? new Intl.NumberFormat("en-US", {
          style: "currency",
          currency: "USD",
        }).format(n)
      : "—";

  const saveDesc = () => {
    onUpdate({ description: descDraft });
    setEditingDesc(false);
  };

  const savePrice = () => {
    const parsed = parseFloat(priceDraft);
    onUpdate({ price: Number.isNaN(parsed) ? null : parsed });
    setEditingPrice(false);
  };

  return (
    <div className="overflow-hidden rounded-lg border border-white/[0.06] bg-white/[0.02]">
      <div
        className="flex cursor-pointer items-center gap-3 px-4 py-3 transition-colors hover:bg-white/[0.02]"
        onClick={() => setExpanded((e) => !e)}
      >
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold">{item.name}</p>
          {!expanded && item.description && (
            <p className="mt-0.5 truncate text-xs text-white/30">
              {item.description}
            </p>
          )}
        </div>

        <div className="shrink-0" onClick={(e) => e.stopPropagation()}>
          {editingPrice ? (
            <div className="flex items-center gap-1">
              <span className="text-sm text-white/40">$</span>
              <input
                type="number"
                value={priceDraft}
                onChange={(e) => setPriceDraft(e.target.value)}
                onBlur={savePrice}
                onKeyDown={(e) => e.key === "Enter" && savePrice()}
                autoFocus
                className="w-20 rounded border border-white/20 bg-white/10 px-2 py-1 text-right text-sm text-white focus:outline-none"
              />
            </div>
          ) : (
            <button
              type="button"
              onClick={() => {
                setEditingPrice(true);
                setPriceDraft(String(item.price ?? ""));
              }}
              className="group flex items-center gap-1 text-sm font-semibold text-white/70 transition-colors hover:text-white"
            >
              {fmt(item.price)}
              <Pencil className="h-3 w-3 text-white/20 transition-colors group-hover:text-white/50" />
            </button>
          )}
        </div>

        <span className="shrink-0 text-white/20">
          {expanded ? (
            <ChevronUp className="h-4 w-4" />
          ) : (
            <ChevronDown className="h-4 w-4" />
          )}
        </span>
      </div>

      {expanded && (
        <div className="space-y-3 border-t border-white/[0.06] px-4 pb-4">
          <div className="pt-3">
            <div className="mb-1.5 flex items-center justify-between">
              <p className="text-xs uppercase tracking-[0.12em] text-white/30">
                Scope description
              </p>
              {!editingDesc ? (
                <button
                  type="button"
                  onClick={() => {
                    setEditingDesc(true);
                    setDescDraft(item.description);
                  }}
                  className="flex items-center gap-1 text-xs text-white/30 transition-colors hover:text-white/60"
                >
                  <Pencil className="h-3 w-3" />
                  Edit
                </button>
              ) : (
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={saveDesc}
                    className="flex items-center gap-1 text-xs text-emerald-400 transition-colors hover:text-emerald-300"
                  >
                    <Check className="h-3 w-3" />
                    Save
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setEditingDesc(false);
                      setDescDraft(item.description);
                    }}
                    className="flex items-center gap-1 text-xs text-white/30 transition-colors hover:text-white/60"
                  >
                    <X className="h-3 w-3" />
                    Cancel
                  </button>
                </div>
              )}
            </div>

            {editingDesc ? (
              <textarea
                value={descDraft}
                onChange={(e) => setDescDraft(e.target.value)}
                rows={4}
                autoFocus
                className="w-full resize-none rounded-lg border border-white/20 bg-white/5 px-3 py-2.5 text-sm text-white placeholder:text-white/20 focus:outline-none focus:ring-1 focus:ring-white/20"
              />
            ) : (
              <p className="text-sm leading-relaxed text-white/60">
                {item.description || (
                  <span className="italic text-white/20">
                    No description yet
                  </span>
                )}
              </p>
            )}
          </div>

          <div className="flex items-center gap-4 pt-1">
            <div className="flex-1">
              <p className="mb-1 text-xs text-white/25">Frequency</p>
              <input
                type="text"
                value={item.frequency ?? ""}
                onChange={(e) => onUpdate({ frequency: e.target.value || null })}
                placeholder="Weekly"
                className="w-full rounded-lg border border-white/[0.06] bg-white/5 px-3 py-1.5 text-sm text-white placeholder:text-white/20 focus:outline-none focus:ring-1 focus:ring-white/20"
              />
            </div>
            <div className="w-28">
              <p className="mb-1 text-xs text-white/25">Duration (hrs)</p>
              <input
                type="number"
                min={0}
                step={0.5}
                value={item.duration_hours ?? ""}
                onChange={(e) =>
                  onUpdate({
                    duration_hours:
                      e.target.value === ""
                        ? null
                        : Number.isNaN(parseFloat(e.target.value))
                          ? null
                          : parseFloat(e.target.value),
                  })
                }
                placeholder="—"
                className="w-full rounded-lg border border-white/[0.06] bg-white/5 px-3 py-1.5 text-sm text-white placeholder:text-white/20 focus:outline-none focus:ring-1 focus:ring-white/20"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ProposalPagePreview({
  icon,
  label,
  description,
  color,
}: {
  icon: React.ReactNode;
  label: string;
  description: string;
  color: string;
}) {
  return (
    <div className={cn("flex items-center gap-3 rounded-lg border px-4 py-3", color)}>
      <div className="shrink-0">{icon}</div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium">{label}</p>
        <p className="mt-0.5 truncate text-xs opacity-70">{description}</p>
      </div>
    </div>
  );
}

function EditableTextarea({
  value,
  placeholder,
  onChange,
  rows = 3,
}: {
  value: string;
  placeholder: string;
  onChange: (val: string) => void;
  rows?: number;
}) {
  return (
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      rows={rows}
      className="w-full resize-none rounded-lg border border-white/[0.06] bg-white/[0.03] px-4 py-3 text-sm text-white/80 placeholder:text-white/20 transition-colors focus:border-white/20 focus:outline-none focus:ring-1 focus:ring-white/20"
    />
  );
}